import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { FileStatus } from '../common/models/filestatus';
import { FileType } from '../common/models/filetype';
import { GatewayType } from '../common/models/gatewaytype';
import { ProductType } from '../common/models/producttype';
import { CommunicationPath } from '../common/models/communicationpath';
import { CpibDeviceTypes } from '../common/models/cpibdevicetypes';
import { Countries } from '../common/models/countries';
import { DeviceGatewayTypes } from '../common/models/DeviceGatewayTypes';

@Injectable({
  providedIn: 'root'
})
export class MasterDataService {
  private readonly masterDataCache = new Map<string, Observable<any>>();

  private readonly getFileStatusApi = this.appConfigService.serverUrl + '/api/v1/file/status';
  private readonly getFileTypeApi = this.appConfigService.serverUrl + '/api/v1/file/type';
  private readonly getGatwayTypeApi = this.appConfigService.serverUrl + '/api/v1/unit/type';
  private readonly getProductTypeApi = this.appConfigService.serverUrl + '/api/v1/unit/producttype';
  private readonly getCommunicationPathApi = this.appConfigService.serverUrl + '/api/v1/unit/communicationpath';
  private readonly getDeviceTypesApi = this.appConfigService.serverUrl + '/api/v1/unit/cpibdevicetypes';
  private readonly getCountriesApi = this.appConfigService.serverUrl + '/api/v1/unit/country';
  private readonly getDeviceGatwayTypeApi = this.appConfigService.serverUrl + '/api/v1/unit/DeviceGatewayType';
  private readonly getEloViewVersionApi = this.appConfigService.serverUrl + '/api/v1/epal/EloViewVersion';
  //Get App Version Api's
  private readonly getVersionInfoApi = this.appConfigService.serverUrl + '/api/v1/master/appInfo';
  
  constructor(private httpClient: HttpClient, private appConfigService: AppConfigService) { }

  private getCachedMasterData<T>(cacheKey: string, requestFactory: () => Observable<T>): Observable<T> {
    const cachedRequest = this.masterDataCache.get(cacheKey);
    if (cachedRequest) {
      return cachedRequest as Observable<T>;
    }

    const request$ = requestFactory().pipe(
      shareReplay(1),
      catchError(error => {
        this.masterDataCache.delete(cacheKey);
        return throwError(() => error);
      })
    );

    this.masterDataCache.set(cacheKey, request$);
    return request$;
  }

  private invalidateMasterDataCache(...cacheKeys: string[]) {
    cacheKeys.forEach(cacheKey => this.masterDataCache.delete(cacheKey));
  }

  clearMasterDataCache() {
    this.masterDataCache.clear();
  }

  preloadMasterData() {
    return forkJoin([
      this.getFileStatus().pipe(catchError(() => of(null))),
      this.getFileType().pipe(catchError(() => of(null))),
      this.getGatewayType().pipe(catchError(() => of(null))),
      this.getProductTypes().pipe(catchError(() => of(null))),
      this.getCommunicationPaths().pipe(catchError(() => of(null))),
      this.getDeviceTypes().pipe(catchError(() => of(null))),
      this.getCountries().pipe(catchError(() => of(null))),
      this.getDeviceGateway().pipe(catchError(() => of(null))),
      this.getEloViewVersions().pipe(catchError(() => of(null))),
      this.getVersionInfo().pipe(catchError(() => of(null)))
    ]).pipe(
      map(() => true)
    );
  }

  getFileStatus() {
    return this.getCachedMasterData('file-status', () => this.httpClient.get(this.getFileStatusApi).pipe(
      map((data: FileStatus[]) => {
        return data;
      })
    ));
  }

  getFileType() {
    return this.getCachedMasterData('file-type', () => this.httpClient.get(this.getFileTypeApi).pipe(
      map((data: FileType[]) => {
        return data;
      })
    ));
  }

  getGatewayType() {
    return this.getCachedMasterData('gateway-type', () => this.httpClient.get(this.getGatwayTypeApi).pipe(
      map((data: GatewayType[]) => {
        return data;
      })
    ));
  }

  getProductTypes() {
    return this.getCachedMasterData('product-types', () => this.httpClient.get(this.getProductTypeApi).pipe(
      map((data: ProductType[]) => {
        return data;
      })
    ));
  }

  getCommunicationPaths() {
    return this.getCachedMasterData('communication-paths', () => this.httpClient.get(this.getCommunicationPathApi).pipe(
      map((data: CommunicationPath[]) => {
        return data;
      })
    ));
  }

  getDeviceTypes(getAllItems: boolean = false) {
    return this.getCachedMasterData('device-types', () => this.httpClient.get(`${this.getDeviceTypesApi}?getAllItems=${getAllItems}`).pipe(
      map((data: CpibDeviceTypes[]) => {
        return data;
      })
    ));
  }

  getDeviceTypes_Provisioning(getAllItems: boolean = false) {
    return this.getCachedMasterData('device-types-provisioning', () => this.httpClient.get(`${this.getDeviceTypesApi}?getAllItems=${getAllItems}`).pipe(
      map((data: CpibDeviceTypes[]) => {
        return data;
      })
    ));
  }

  getCountries() {
    return this.getCachedMasterData('countries', () => this.httpClient.get(this.getCountriesApi).pipe(
      map((data: Countries[]) => {
        return data;
      })
    ));
  }

  getDeviceGateway() {
    return this.getCachedMasterData('device-gateway', () => this.httpClient.get(this.getDeviceGatwayTypeApi).pipe(
      map((data: DeviceGatewayTypes[]) => {
        return data;
      })
    ));
  }

  getEloViewVersions() {
    return this.getCachedMasterData('eloview-versions', () => this.httpClient.get<any[]>(this.getEloViewVersionApi).pipe(
      map((data: any[]) => {
        return data;
      })
    ));
  }

  getVersionInfo() {
    return this.getCachedMasterData('version-info', () => this.httpClient.get(this.getVersionInfoApi).pipe(
      map((data: any) => {
        return data;
      })
    ));
  }  
}